/**
 * Statut « Sous option » — pause de vitrine réversible.
 *
 * Modèle (décision 2026-07-07) : overlay booléen posé PAR-DESSUS un bien qui
 * reste `disponibilite = DISPONIBLE`. Cocher « sous option » retire le bien des
 * sites, d'Immoweb et du mailing, mais ne touche NI le cycle ELS/PEB, NI le
 * locataire, NI le suivi (contrairement à LOUÉ/VENDU). Décocher = de nouveau en
 * ligne, sans paperasse.
 *
 * Ce module porte la RÈGLE D'ÉLIGIBILITÉ de la case + les PATCHES de mise sous
 * option / de levée (dépublication réversible). Le miroir serveur (trigger DB
 * migration 084) applique la même logique à partir de `sousOption` seul, pour
 * rester correct même quand le RPC filtre les colonnes internes (non-admin).
 */

/** Valeurs du formulaire (kebab) — pas les valeurs DB (MAJUSCULES). */
export interface SousOptionEligibilityInput {
  /** `transaction` du bien (enum form). */
  transaction?: string | null;
  /** `disponibilite` au format formulaire ("disponible", "loue", …). */
  disponibilite?: string | null;
}

/**
 * La case « Sous option » n'est cochable que pour un bien en LOCATION dont le
 * statut est DISPONIBLE. Tout autre statut (loué, vendu, en préparation) ou
 * transaction (vente — traitée plus tard avec le workflow vente) verrouille
 * la case.
 */
export function canToggleSousOption(input: SousOptionEligibilityInput): boolean {
  return input.transaction === "location" && input.disponibilite === "disponible";
}

/** Patch partiel appliqué au form (et miroir de ce que fait le trigger DB). */
export interface SousOptionPatch {
  sousOption: boolean;
  sousOptionDepuis: string;
  sousOptionStatutAvant?: string;
  statutPublication?: string;
}

/**
 * MISE sous option. Horodate l'entrée et, si le bien était `Publié`, le dépublie
 * (statutPublication → Retiré) en MÉMORISANT le statut d'avant dans
 * `sousOptionStatutAvant` — c'est ce qui permet de ne republier au décochage QUE
 * si c'est bien la mise sous option qui avait dépublié.
 *
 * @param statutPublication statut de publication courant
 * @param today             date ISO (YYYY-MM-DD) — injectée pour rester testable
 * @param existingDepuis    date de sous option déjà présente (on ne l'écrase pas)
 */
export function sousOptionEnterPatch(
  statutPublication: string | null | undefined,
  today: string,
  existingDepuis?: string | null,
): SousOptionPatch {
  const wasPublie = statutPublication === "Publié";
  return {
    sousOption: true,
    sousOptionDepuis: existingDepuis || today,
    ...(wasPublie ? { statutPublication: "Retiré", sousOptionStatutAvant: "Publié" } : {}),
  };
}

/**
 * LEVÉE de la sous option. Remet en ligne UNIQUEMENT si `sousOptionStatutAvant`
 * est renseigné (= c'est nous qui avions dépublié) ; un bien retiré manuellement
 * avant la mise sous option n'est donc pas republié par erreur. Le champ mémoire
 * est toujours remis à vide.
 */
export function sousOptionExitPatch(
  sousOptionStatutAvant: string | null | undefined,
): SousOptionPatch {
  const restore = !!sousOptionStatutAvant;
  return {
    sousOption: false,
    sousOptionDepuis: "",
    sousOptionStatutAvant: "",
    ...(restore ? { statutPublication: sousOptionStatutAvant as string } : {}),
  };
}
